/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { PopupComponent } from '../popup/popup.component';
import { ArcgisService } from '../../../pages/dashboard/services/arcgis/arcgis.service';
import { AuditLogService } from '../../services/audit-log/audit-log.service';
import { takeUntil } from 'rxjs';
import { DestroyBase } from '../../base-classes/destroy-component-base';
import { SearchService } from '../../../pages/search/services/search.service';
import { toolkitIntelligenceLayers } from '../../../pages/dashboard/configs/Site-Toolkit-filters-config';
import { isNullOrUndefined } from '../../utils/utils';

interface IntersectionDataSection {
  name: string;
  data: Record<string, any>;
  hasData: boolean;
}

@Component({
  selector: 'app-record-arcgis',
  standalone: true,
  imports: [CommonModule, PopupComponent],
  templateUrl: './record-arcgis.component.html',
  styleUrl: './record-arcgis.component.scss',
})
export class RecordArcgisComponent
  extends DestroyBase
  implements OnInit, OnDestroy
{
  @Input() latitude!: number;
  @Input() longitude!: number;
  @Input() recordName = '';
  @Input() uniqueId!: string;
  @Input() preloadedData?: Record<string, unknown>;
  @Output() closePopup = new EventEmitter<void>();

  arcgisService = inject(ArcgisService);
  auditLogService = inject(AuditLogService);
  router = inject(Router);
  recordService = inject(SearchService);

  isLoading = false;
  errorMessage = '';
  intersectionData: IntersectionDataSection[] = [];
  expandedSections: Record<string, boolean> = {};

  // Loading messages that cycle every second
  loadingMessages = [
    'Connecting to map services...',
    'Querying spatial layers...',
    'Running intersection analysis...',
    'Validating geometry data...',
    'Compiling results...',
    'Almost there...',
  ];
  currentLoadingMessage = this.loadingMessages[0];
  private loadingMessageInterval?: ReturnType<typeof setInterval>;

  // Property display name mapping from toolkit config
  private propertyDisplayNameMap = new Map<string, string>();

  ngOnInit(): void {
    this.buildPropertyDisplayNameMap();

    // If data is already provided, use it instead of making API call
    if (this.preloadedData) {
      this.processIntersectionData(this.preloadedData);
      this.expandedSections['Program Details'] = true;
    } else {
      this.loadIntersectionData();
    }
  }

  private buildPropertyDisplayNameMap(): void {
    // Build a map of all property keys to their display names from the config
    toolkitIntelligenceLayers.forEach((layer) => {
      if (layer.fields) {
        layer.fields.forEach((field) => {
          this.propertyDisplayNameMap.set(field.key, field.displayName);
        });
      }
      // Handle sublayers for Rural Designations
      if (layer.subLayers) {
        layer.subLayers.forEach((subLayer) => {
          if (subLayer.fields) {
            subLayer.fields.forEach((field) => {
              this.propertyDisplayNameMap.set(field.key, field.displayName);
            });
          }
        });
      }
    });
  }

  override ngOnDestroy(): void {
    this.clearLoadingInterval();
    super.ngOnDestroy();
  }

  private startLoadingMessageCycle(): void {
    this.clearLoadingInterval();
    let messageIndex = 0;
    this.currentLoadingMessage = this.loadingMessages[0];

    this.loadingMessageInterval = setInterval(() => {
      messageIndex++;
      if (messageIndex < this.loadingMessages.length) {
        this.currentLoadingMessage = this.loadingMessages[messageIndex];
      } else {
        // Reached the last message, stop cycling
        this.clearLoadingInterval();
      }
    }, 1500);
  }

  private clearLoadingInterval(): void {
    if (this.loadingMessageInterval) {
      clearInterval(this.loadingMessageInterval);
      this.loadingMessageInterval = undefined;
    }
  }

  loadIntersectionData(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.startLoadingMessageCycle();

    this.recordService
      .getRecordArcgisIntersectionData(this.uniqueId!)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.processIntersectionData(response);
          // Expand Program Details section by default
          this.expandedSections['Program Details'] = true;
          this.isLoading = false;
          this.clearLoadingInterval();
        },
        error: (error) => {
          console.error('Error loading intersection data:', error);
          this.errorMessage =
            'Failed to load location intelligence data. Please try again.';
          this.isLoading = false;
          this.clearLoadingInterval();
        },
      });
  }

  processIntersectionData(data: Record<string, any>): void {
    const customSections: IntersectionDataSection[] = [];

    const getLayerValueByKey = (key: string): Record<string, any> => {
      return data?.[key]?.value ?? {};
    };

    // Get layer data
    const cdfiData = getLayerValueByKey('cdfi_investment_areas');

    const lmiData = getLayerValueByKey('low_to_moderate_income_areas');

    const regridData = getLayerValueByKey('parcels');

    const ddaData = getLayerValueByKey('difficult_development_areas');

    const recaData = getLayerValueByKey('racial_poverty_areas');

    const ruralOMBData = getLayerValueByKey('non_metro_rural_areas');

    const priorityEnergyCommunitiesData = getLayerValueByKey(
      'energy_community_rural_areas',
    );

    const rucaRuralAreasData = getLayerValueByKey('usda_rural_defined_areas');

    const acsData = getLayerValueByKey('demographics');

    // Program Details
    const programDetailsData: Record<string, any> = {};

    if (cdfiData['ia2020'] !== undefined && cdfiData['ia2020'] !== null) {
      programDetailsData['CDFI Investment Area 2023 (ia2020)'] =
        cdfiData['ia2020'];
    }

    const lowModPct = lmiData['LOWMODPCT'];
    if (lowModPct !== undefined && lowModPct !== null) {
      const numericValue =
        typeof lowModPct === 'number' ? lowModPct : parseFloat(lowModPct);

      programDetailsData['LMI Areas'] =
        !isNaN(numericValue) && numericValue > 51 ? 'Yes' : 'No';
    }

    const regridProperties = [
      'qoz',
      'qoz_tract',
      'census_tract',
      'census_block',
      'census_zcta',
    ];

    regridProperties.forEach((prop) => {
      programDetailsData[prop] = regridData[prop];
    });

    const ddaProperties = ['DDA_CODE', 'DDA_TYPE', 'DDA_NAME'];
    const ddaNestedData: Record<string, any> = {};

    ddaProperties.forEach((prop) => {
      ddaNestedData[prop] = ddaData[prop];
    });

    if (Object.keys(ddaNestedData).length > 0) {
      programDetailsData['_nested_Difficult Development Areas'] = ddaNestedData;
    }

    const recaProperties = ['rcap_current', 'RCAP_90', 'RCAP_00', 'RCAP_10'];

    const recaNestedData: Record<string, any> = {};

    recaProperties.forEach((prop) => {
      recaNestedData[prop] = recaData[prop];
    });

    if (Object.keys(recaNestedData).length > 0) {
      programDetailsData['_nested_R/ECAPs'] = recaNestedData;
    }

    if (Object.keys(programDetailsData).length > 0) {
      customSections.push({
        name: 'Program Details',
        data: programDetailsData,
        hasData: true,
      });
    }

    // Rural Designations
    const ruralDesignationsData: Record<string, any> = {};

    ruralDesignationsData['OMB NonMetro Rural Areas'] =
      ruralOMBData['description'];

    ruralDesignationsData['Priority Energy Communities Rural Areas'] =
      isNullOrUndefined(priorityEnergyCommunitiesData['objectid'])
        ? 'N/A'
        : 'Yes';

    ruralDesignationsData['USDA RUCA Rural Areas'] = isNullOrUndefined(
      rucaRuralAreasData['objectid'],
    )
      ? 'N/A'
      : 'Yes';

    if (Object.keys(ruralDesignationsData).length > 0) {
      customSections.push({
        name: 'Rural Designations',
        data: ruralDesignationsData,
        hasData: true,
      });
    }

    // Climate Info
    const climateInfoData: Record<string, any> = {};

    const femaProperties = [
      'fema_flood_zone',
      'fema_flood_zone_subtype',
      'fema_flood_zone_raw',
      'fema_flood_zone_data_date',
      'fema_nri_risk_rating',
    ];

    femaProperties.forEach((prop) => {
      climateInfoData[prop] = regridData[prop];
    });

    if (Object.keys(climateInfoData).length > 0) {
      customSections.push({
        name: 'Climate Info',
        data: climateInfoData,
        hasData: true,
      });
    }

    // Owner Details
    const ownerDetailsSection: Record<string, any> = {};

    const ownerProperties = [
      'owntype',
      'owner',
      'unmodified_owner',
      'ownfrst',
      'ownlast',
      'owner2',
      'owner3',
      'owner4',
    ];

    ownerProperties.forEach((prop) => {
      ownerDetailsSection[prop] = regridData[prop];
    });

    if (Object.keys(ownerDetailsSection).length > 0) {
      customSections.push({
        name: 'Owner Details',
        data: ownerDetailsSection,
        hasData: true,
      });
    }

    // Parcel Value
    const parcelValueSection: Record<string, any> = {};

    const parcelValueProperties = [
      'parval',
      'parvaltype',
      'saleprice',
      'saledate',
      'improvval',
      'landval',
      'agval',
      'taxamt',
      'taxyear',
    ];

    parcelValueProperties.forEach((prop) => {
      parcelValueSection[prop] = regridData[prop];
    });

    if (Object.keys(parcelValueSection).length > 0) {
      customSections.push({
        name: 'Parcel Value',
        data: parcelValueSection,
        hasData: true,
      });
    }

    // Parcel Details
    const parcelDetailsSection: Record<string, any> = {};

    const parcelDetailsProperties = [
      'usecode',
      'usedesc',
      'zoning_type',
      'zoning_subtype',
      'zoning_code_link',
      'structno',
      'yearbuilt',
      'numstories',
      'structstyle',
      'mail_addno',
      'mail_addpref',
      'mail_addstr',
      'mail_addsttyp',
      'mail_addstsuf',
      'mail_city',
      'mail_state2',
      'mail_zip',
      'mail_country',
      'mail_unit',
      'book',
      'page',
      'neighborhood',
      'subdivision',
      'lat',
      'lon',
      'll_last_refresh',
      'sourceurl',
      'recrdareano',
      'deeded_acres',
      'gisacre',
      'll_row_parcel',
      'll_updated_at',
      'lbcs_activity',
      'lbcs_activity_desc',
      'lbcs_function',
      'lbcs_function_desc',
      'lbcs_ownership',
      'population_growth_past_5_years',
      'transmission_line_distance',
    ];

    parcelDetailsProperties.forEach((prop) => {
      parcelDetailsSection[prop] = regridData[prop];
    });

    if (Object.keys(parcelDetailsSection).length > 0) {
      customSections.push({
        name: 'Parcel Details',
        data: parcelDetailsSection,
        hasData: true,
      });
    }

    // ACS Section
    customSections.push({
      name: 'ACS 5YR Demographic Estimate Data by Tract',
      data: acsData,
      hasData: true,
    });

    this.intersectionData = customSections;
  }

  formatSectionName(name: string): string {
    // Format section names for display
    return name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  formatPropertyName(name: string): string {
    // Check if we have a display name from the config
    const displayName = this.propertyDisplayNameMap.get(name);
    if (displayName) {
      return displayName;
    }

    // Fallback to default formatting
    return name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  formatPropertyValue(value: any, key?: string): string {
    if (value === null || value === undefined) {
      return 'N/A';
    }

    // Check if this is a date field (Unix timestamp in milliseconds)
    const dateFields = [
      'fema_flood_zone_data_date',
      'saledate',
      'll_last_refresh',
      'll_updated_at',
    ];
    if (key && dateFields.includes(key) && typeof value === 'number') {
      try {
        const date = new Date(value);
        // Check if date is valid
        if (!isNaN(date.getTime())) {
          // Format as DD Month YYYY (e.g., 04 April 2024)
          const day = date.getDate().toString().padStart(2, '0');
          const month = date.toLocaleDateString('en-US', { month: 'long' });
          const year = date.getFullYear();
          return `${day} ${month} ${year}`;
        }
      } catch (error) {
        console.error('Error formatting date:', error);
      }
    }

    if (typeof value === 'number') {
      // Format numbers with commas
      return value.toLocaleString();
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value);
  }

  toggleSection(sectionName: string): void {
    this.expandedSections[sectionName] = !this.expandedSections[sectionName];
  }

  isSectionExpanded(sectionName: string): boolean {
    return this.expandedSections[sectionName] || false;
  }

  getDataProperties(data: Record<string, any>): { key: string; value: any }[] {
    return Object.entries(data)
      .filter(
        ([key]) => !key.startsWith('Shape__') && !key.startsWith('SHAPE__'),
      )
      .map(([key, value]) => ({ key, value }));
  }

  isNestedProperty(key: string): boolean {
    return key.startsWith('_nested_');
  }

  getNestedPropertyName(key: string): string {
    return key.replace('_nested_', '');
  }

  isNestedPropertyExpanded(sectionName: string, nestedKey: string): boolean {
    const fullKey = `${sectionName}_${nestedKey}`;
    return this.expandedSections[fullKey] || false;
  }

  toggleNestedProperty(sectionName: string, nestedKey: string): void {
    const fullKey = `${sectionName}_${nestedKey}`;
    this.expandedSections[fullKey] = !this.expandedSections[fullKey];
  }

  getNestedProperties(
    data: Record<string, any>,
  ): { key: string; value: any }[] {
    return Object.entries(data)
      .filter(
        ([key]) => !key.startsWith('Shape__') && !key.startsWith('SHAPE__'),
      )
      .map(([key, value]) => ({ key, value }));
  }

  onViewOnMap(): void {
    // Log audit event
    if (this.uniqueId) {
      this.auditLogService.logFeature({
        featureName: 'location intelligence of record',
        action: 'click',
        meta: {
          recordName: this.recordName || '',
          recordId: this.uniqueId,
        },
        description: 'User clicks for location intelligence of record',
      });
    }

    // Check if dashboard is available
    const dashboards = this.arcgisService.getDashboardsFromStorage();
    if (!dashboards || !dashboards?.['siteToolkit']) {
      console.error('Site Toolkit dashboard not found');
      return;
    }

    // Navigate to site intelligence
    this.router
      .navigate(['/dashboard'], {
        queryParams: {
          map: 'site-toolkit',
          activeTab: 'siteIntelligence',
          lat: this.latitude,
          lon: this.longitude,
          leadId: this.uniqueId,
        },
      })
      .then(() => {
        // Close the popup after successful navigation
        this.closePopup.emit();
      })
      .catch((error) => {
        console.error('Navigation failed:', error);
      });
  }

  onClose(): void {
    this.closePopup.emit();
  }

  retry(): void {
    this.loadIntersectionData();
  }
}
